import type { RefObject } from "react";
import { useState } from "react";
import { useAnalyticsContext } from "./AnalyticsContextProvider.tsx";
import { useTracking } from "./TrackingProvider.tsx";
import type { IntersectionObserverOptions } from "./use-intersection-observer.ts";
import { useIntersectionObserver } from "./use-intersection-observer.ts";

type ImpressionEventName =
  | "PAGE_VIEW"
  | "SECTION_VIEW"
  | "DIALOG_VIEW"
  | "IMAGE_VIEW";

export type ImpressionTrackingProps<Element extends HTMLElement> = {
  properties?: Record<string, unknown>;
  ref?: RefObject<Element>;
} & Omit<IntersectionObserverOptions, "onChange">;

/**
 * `useImpressionTracking` is a hook that tracks the visibility of an element.
 * It uses the Intersection Observer API to detect when the element becomes
 * visible in the viewport. When the element becomes visible, it fires an
 * analytics event.
 */
export function useImpressionTracking<
  Element extends HTMLElement = HTMLElement,
>(
  eventName: ImpressionEventName,
  { properties, ...options }: ImpressionTrackingProps<Element>,
) {
  // Note that it's important to use useState here instead of useRef, because
  // the ref object is used in the useEffect dependency array. If we used
  // useRef, the ref object would never be updated, and the effect would not
  // re-run when the ref changes.
  const [element, setElement] = useState<Element | null>(null);

  const track = useTracking();
  const parentProperties = useAnalyticsContext();

  useIntersectionObserver(element, {
    onChange(visible) {
      if (visible) {
        track(eventName, { ...parentProperties, ...properties });
      }
    },
    ...options,
  });

  return setElement;
}
